import { Platform, PlatformDetectionResult } from '@/types'
import { parseMoneyAmount } from '@/lib/money'

// Import mapping configurations
import boltMappings from '@/config/columnMappings/boltMappings.json'
import uberMappings from '@/config/columnMappings/uberMappings.json'
import sumupMappings from '@/config/columnMappings/sumupMappings.json'
import genericMappings from '@/config/columnMappings/genericMappings.json'

/**
 * LEGACY SERVICE - DEPRECATED
 *
 * Unified Column Mapping Service - REPLACED BY ColumnMappingService
 *
 * This service has been replaced by ColumnMappingService which provides:
 * - Better platform detection integration
 * - Cleaner architecture
 * - Improved column mapping accuracy
 *
 * TODO: Remove this service after all references are updated to ColumnMappingService
 *
 * This service provides intelligent column detection and mapping for CSV files.
 * It combines both identifier detection (driver names, IDs) and earnings detection
 * (gross/net earnings, tips, bonuses) into a single unified system.
 */

// Core types
export interface ColumnMappingConfig {
  platform: string
  identifierFields: Record<string, string[]>
  earningsFields: Record<string, string[]>
  requiredFields: {
    identifierFields: string[]
    earningsFields: string[]
  }
  optionalFields: {
    identifierFields: string[]
    earningsFields: string[]
  }
}

export interface UnifiedColumnMapping {
  identifierFields: Record<string, string> // internal field -> CSV header
  earningsFields: Record<string, string>   // internal field -> CSV header
  confidence: number                       // 0-100
  detectedPlatform?: string
  manualOverride: boolean
  unmappedHeaders: string[]
  missingRequiredFields: string[]
}

export interface NormalizedHeader {
  original: string
  normalized: string
  tokens: string[]
}

export interface FieldMatch {
  internalField: string
  headerName: string
  confidence: number
  reason: string
}

export interface DetectionResult {
  platformMatches: Array<{
    platform: string
    confidence: number
    mapping: UnifiedColumnMapping
  }>
  bestMatch: UnifiedColumnMapping
  suggestions: FieldMatch[]
}

/**
 * @deprecated This interface has been moved to @/types/index.ts
 * Import PlatformDetectionResult from '@/types' instead.
 * This duplicate interface is scheduled for removal.
 */
/*
export interface PlatformDetectionResult {
  detectedPlatform: string | null
  confidence: number
  allMatches: Array<{
    platform: string
    confidence: number
    reason: string
  }>
  shouldAutoSelect: boolean
  suggestedDefault?: string
}
*/

// Earnings data extraction and validation types
export interface EarningsData {
  grossEarnings: number
  netEarnings: number
  tips: number
  driverBonus?: number
  actualVAT?: number
  cashReceived?: number
}

export interface EarningsValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
}

export class UnifiedColumnMappingService {
  private static mappingConfigs: Record<string, ColumnMappingConfig> = {
    bolt: boltMappings as ColumnMappingConfig,
    uber: uberMappings as ColumnMappingConfig,
    sumup: sumupMappings as ColumnMappingConfig,
    generic: genericMappings as ColumnMappingConfig
  }

  /**
   * Normalize header names for better matching
   */
  static normalizeHeader(header: string): NormalizedHeader {
    // Step 1: Basic cleaning
    let normalized = header.toLowerCase()
      .trim()
      .replace(/[_\-\s]+/g, ' ') // Replace underscores, hyphens, spaces with single space
      .replace(/[^\w\såäö]/g, '') // Remove special chars except Swedish chars
      .trim()

    // Step 2: Remove accents/diacritics
    normalized = normalized
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[ýÿ]/g, 'y')
      .replace(/[ñ]/g, 'n')
      .replace(/[ç]/g, 'c')

    // Step 3: Create tokens for partial matching
    const tokens = normalized.split(' ').filter(token => token.length > 0)

    return {
      original: header,
      normalized,
      tokens
    }
  }

  /**
   * Calculate similarity between two normalized strings using Levenshtein distance
   */
  private static calculateSimilarity(str1: string, str2: string): number {
    // Exact match
    if (str1 === str2) return 1.0

    // Levenshtein distance based similarity
    const maxLength = Math.max(str1.length, str2.length)
    if (maxLength === 0) return 1.0

    const distance = this.levenshteinDistance(str1, str2)
    return Math.max(0, 1 - (distance / maxLength))
  }

  /**
   * Calculate Levenshtein distance between two strings
   */
  private static levenshteinDistance(str1: string, str2: string): number {
    const matrix: number[][] = []

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i]
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1]
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1,     // insertion
            matrix[i - 1][j] + 1      // deletion
          )
        }
      }
    }

    return matrix[str2.length][str1.length]
  }

  /**
   * Calculate token-based matching score for compound headers
   */
  private static calculateTokenMatch(tokens1: string[], tokens2: string[]): number {
    if (tokens1.length === 0 && tokens2.length === 0) return 1.0
    if (tokens1.length === 0 || tokens2.length === 0) return 0.0

    let matches = 0
    const maxTokens = Math.max(tokens1.length, tokens2.length)

    for (const token1 of tokens1) {
      for (const token2 of tokens2) {
        if (token1 === token2 || this.calculateSimilarity(token1, token2) > 0.8) {
          matches++
          break
        }
      }
    }

    return matches / maxTokens
  }

  /**
   * Find best match for a header against a field's synonyms
   */
  private static findBestFieldMatch(
    normalizedHeader: NormalizedHeader,
    fieldSynonyms: string[]
  ): { confidence: number; reason: string } {
    let bestConfidence = 0
    let bestReason = ''

    for (const synonym of fieldSynonyms) {
      const normalizedSynonym = this.normalizeHeader(synonym)
      
      // Exact match
      if (normalizedHeader.normalized === normalizedSynonym.normalized) {
        return { confidence: 1.0, reason: `Exact match: "${synonym}"` }
      }

      // String similarity
      const similarity = this.calculateSimilarity(
        normalizedHeader.normalized,
        normalizedSynonym.normalized
      )

      if (similarity > bestConfidence && similarity > 0.7) {
        bestConfidence = similarity
        bestReason = `Similar to: "${synonym}" (${Math.round(similarity * 100)}%)`
      }

      // Token-based matching
      const tokenMatch = this.calculateTokenMatch(
        normalizedHeader.tokens, 
        normalizedSynonym.tokens
      )

      if (tokenMatch > bestConfidence && tokenMatch > 0.6) {
        bestConfidence = tokenMatch
        bestReason = `Token match: "${synonym}" (${Math.round(tokenMatch * 100)}%)`
      }

      // Substring matching for shorter synonyms
      if (synonym.length >= 3) {
        const isSubstring = normalizedHeader.normalized.includes(normalizedSynonym.normalized) ||
                           normalizedSynonym.normalized.includes(normalizedHeader.normalized)

        if (isSubstring && 0.5 > bestConfidence) {
          bestConfidence = 0.5
          bestReason = `Contains: "${synonym}"`
        }
      }
    }

    return { confidence: bestConfidence, reason: bestReason }
  }

  /**
   * Test mapping against a specific platform configuration
   */
  private static testPlatformMapping(
    headers: string[],
    config: ColumnMappingConfig
  ): UnifiedColumnMapping {
    const normalizedHeaders = headers.map(h => this.normalizeHeader(h))
    
    const identifierFields: Record<string, string> = {}
    const earningsFields: Record<string, string> = {}
    const unmappedHeaders: string[] = []
    const missingRequiredFields: string[] = []
    
    let totalScore = 0
    let maxPossibleScore = 0

    // Map identifier fields
    for (const [fieldKey, synonyms] of Object.entries(config.identifierFields)) {
      const isRequired = config.requiredFields.identifierFields.includes(fieldKey)
      const weight = isRequired ? 2 : 1
      maxPossibleScore += weight

      let bestMatch = { headerIndex: -1, confidence: 0, reason: '' }

      // Test each header
      for (let i = 0; i < normalizedHeaders.length; i++) {
        const match = this.findBestFieldMatch(normalizedHeaders[i], synonyms)
        if (match.confidence > bestMatch.confidence) {
          bestMatch = { headerIndex: i, ...match }
        }
      }

      // If good match found, use it
      if (bestMatch.confidence >= 0.6) {
        identifierFields[fieldKey] = headers[bestMatch.headerIndex]
        totalScore += weight * bestMatch.confidence
      } else if (isRequired) {
        missingRequiredFields.push(fieldKey)
      }
    }

    // Check for alternative driver name requirement satisfaction
    // If driverName is missing but firstName and lastName are both mapped, remove driverName from missing required fields
    if (missingRequiredFields.includes('driverName') &&
        identifierFields.firstName &&
        identifierFields.lastName) {
      const driverNameIndex = missingRequiredFields.indexOf('driverName')
      missingRequiredFields.splice(driverNameIndex, 1)

      // Add score for the satisfied requirement (treat as if driverName was found with high confidence)
      const driverNameWeight = 2 // driverName is typically required, so weight = 2
      totalScore += driverNameWeight * 0.9 // High confidence for firstName + lastName combination
    }

    // Map earnings fields
    for (const [fieldKey, synonyms] of Object.entries(config.earningsFields)) {
      const isRequired = config.requiredFields.earningsFields.includes(fieldKey)
      const weight = isRequired ? 2 : 1
      maxPossibleScore += weight

      let bestMatch = { headerIndex: -1, confidence: 0, reason: '' }

      // Test each header
      for (let i = 0; i < normalizedHeaders.length; i++) {
        // Skip if already used by identifier fields
        if (Object.values(identifierFields).includes(headers[i])) continue

        const match = this.findBestFieldMatch(normalizedHeaders[i], synonyms)
        if (match.confidence > bestMatch.confidence) {
          bestMatch = { headerIndex: i, ...match }
        }
      }

      // If good match found, use it
      if (bestMatch.confidence >= 0.6) {
        earningsFields[fieldKey] = headers[bestMatch.headerIndex]
        totalScore += weight * bestMatch.confidence
      } else if (isRequired) {
        missingRequiredFields.push(fieldKey)
      }
    }

    // Find unmapped headers
    const mappedHeaders = [
      ...Object.values(identifierFields),
      ...Object.values(earningsFields)
    ]

    headers.forEach(header => {
      if (!mappedHeaders.includes(header)) {
        unmappedHeaders.push(header)
      }
    })

    // Calculate confidence score
    const baseConfidence = maxPossibleScore > 0 ? (totalScore / maxPossibleScore) * 100 : 0

    // Apply penalties for missing required fields
    const requiredFieldPenalty = missingRequiredFields.length * 15
    const finalConfidence = Math.max(0, Math.round(baseConfidence - requiredFieldPenalty))

    return {
      identifierFields,
      earningsFields,
      confidence: finalConfidence,
      detectedPlatform: config.platform,
      manualOverride: false,
      unmappedHeaders,
      missingRequiredFields
    }
  }

  /**
   * Detect column mappings from CSV headers
   */
  static detectColumnMappings(
    headers: string[], 
    platformHint?: Platform
  ): DetectionResult {
    const platformMatches: Array<{
      platform: string
      confidence: number
      mapping: UnifiedColumnMapping
    }> = []

    // Test specific platform first if hint provided
    if (platformHint) {
      // Map "other" platform to "generic" configuration
      const configKey = platformHint === 'other' ? 'generic' : platformHint
      if (this.mappingConfigs[configKey]) {
        const mapping = this.testPlatformMapping(headers, this.mappingConfigs[configKey])
        platformMatches.push({
          platform: platformHint,
          confidence: mapping.confidence,
          mapping
        })
      }
    }

    // Test all other platforms
    for (const [platform, config] of Object.entries(this.mappingConfigs)) {
      if (platform === platformHint) continue // Skip if already tested

      const mapping = this.testPlatformMapping(headers, config)
      platformMatches.push({
        platform,
        confidence: mapping.confidence,
        mapping
      })
    }

    // Sort by confidence
    platformMatches.sort((a, b) => b.confidence - a.confidence)

    // Best match is the highest confidence
    const bestMatch = platformMatches[0]?.mapping || {
      identifierFields: {},
      earningsFields: {},
      confidence: 0,
      manualOverride: false,
      unmappedHeaders: headers,
      missingRequiredFields: []
    }

    return {
      platformMatches,
      bestMatch,
      suggestions: [] // TODO: Implement suggestions for manual mapping
    }
  }

  /**
   * Get manual mapping options for user override
   */
  static getManualMappingOptions(
    headers: string[],
    currentMapping: UnifiedColumnMapping
  ): {
    availableHeaders: string[]
    unmappedIdentifierFields: string[]
    unmappedEarningsFields: string[]
    suggestions: Record<string, string[]>
  } {
    const mappedHeaders = [
      ...Object.values(currentMapping.identifierFields),
      ...Object.values(currentMapping.earningsFields)
    ]
    
    const availableHeaders = headers.filter(h => !mappedHeaders.includes(h))
    
    // Find unmapped fields from best matching platform
    const platformConfig = this.mappingConfigs[currentMapping.detectedPlatform || 'generic']
    
    const allIdentifierFields = Object.keys(platformConfig.identifierFields)
    const allEarningsFields = Object.keys(platformConfig.earningsFields)
    
    const unmappedIdentifierFields = allIdentifierFields.filter(
      field => !currentMapping.identifierFields[field]
    )
    const unmappedEarningsFields = allEarningsFields.filter(
      field => !currentMapping.earningsFields[field]
    )
    
    // Generate suggestions (simplified for now)
    const suggestions: Record<string, string[]> = {}
    
    return {
      availableHeaders,
      unmappedIdentifierFields,
      unmappedEarningsFields,
      suggestions
    }
  }

  /**
   * Create a manual mapping override
   */
  static createManualMapping(
    identifierFields: Record<string, string>,
    earningsFields: Record<string, string>,
    headers: string[],
    detectedPlatform?: string
  ): UnifiedColumnMapping {
    const mappedHeaders = [
      ...Object.values(identifierFields),
      ...Object.values(earningsFields)
    ]
    
    const unmappedHeaders = headers.filter(h => !mappedHeaders.includes(h))
    
    // Check for missing required fields
    const platformConfig = this.mappingConfigs[detectedPlatform || 'generic']
    const missingRequiredFields = [
      ...platformConfig.requiredFields.identifierFields.filter(
        field => !identifierFields[field]
      ),
      ...platformConfig.requiredFields.earningsFields.filter(
        field => !earningsFields[field]
      )
    ]

    return {
      identifierFields,
      earningsFields,
      confidence: 100, // Manual mapping is 100% confident
      detectedPlatform,
      manualOverride: true,
      unmappedHeaders,
      missingRequiredFields
    }
  }

  /**
   * Validate a column mapping
   */
  static validateMapping(mapping: UnifiedColumnMapping): {
    isValid: boolean
    errors: string[]
    warnings: string[]
  } {
    const errors: string[] = []
    const warnings: string[] = []

    // Check for required fields
    if (mapping.missingRequiredFields.length > 0) {
      errors.push(`Obligatoriska fält saknas: ${mapping.missingRequiredFields.join(', ')}`)
    }

    // Check for duplicate header usage
    const allMappedHeaders = [
      ...Object.values(mapping.identifierFields),
      ...Object.values(mapping.earningsFields)
    ]

    const duplicates = allMappedHeaders.filter((header, index) =>
      allMappedHeaders.indexOf(header) !== index
    )

    if (duplicates.length > 0) {
      errors.push(`Samma kolumn används för flera fält: ${duplicates.join(', ')}`)
    }

    // Check confidence level
    if (mapping.confidence < 60 && !mapping.manualOverride) {
      warnings.push(`Låg säkerhet för automatisk detektering (${mapping.confidence}%)`)
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    }
  }

  /**
   * Extract earnings data from a CSV row using the unified mapping
   */
  static extractEarningsData(row: any, mapping: UnifiedColumnMapping): EarningsData {
    const data: EarningsData = {
      grossEarnings: 0,
      netEarnings: 0,
      tips: 0,
      driverBonus: 0,
      actualVAT: 0
    }

    // Helper function to safely parse numeric values
    const parseNumber = (value: any): number => {
      if (value === null || value === undefined || value === '') return 0

      const parsed = parseMoneyAmount(value)
      return parsed === null ? 0 : Math.abs(parsed)
    }

    // Extract earnings fields using the unified mapping
    const { earningsFields } = mapping

    if (earningsFields.grossEarnings && row[earningsFields.grossEarnings] !== undefined) {
      data.grossEarnings = parseNumber(row[earningsFields.grossEarnings])
    }

    if (earningsFields.netEarnings && row[earningsFields.netEarnings] !== undefined) {
      data.netEarnings = parseNumber(row[earningsFields.netEarnings])
    }

    if (earningsFields.tips && row[earningsFields.tips] !== undefined) {
      data.tips = parseNumber(row[earningsFields.tips])
    }

    if (earningsFields.driverBonus && row[earningsFields.driverBonus] !== undefined) {
      data.driverBonus = parseNumber(row[earningsFields.driverBonus])
    }

    if (earningsFields.actualVAT && row[earningsFields.actualVAT] !== undefined) {
      data.actualVAT = parseNumber(row[earningsFields.actualVAT])
    }

    return data
  }

  /**
   * Validate extracted earnings data for logical consistency
   */
  static validateEarningsData(data: EarningsData): EarningsValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    // Basic validation
    if (data.grossEarnings < 0) {
      errors.push('Bruttoinkomst kan inte vara negativ')
    }

    if (data.netEarnings < 0) {
      errors.push('Nettoinkomst kan inte vara negativ')
    }

    if (data.tips < 0) {
      errors.push('Dricks kan inte vara negativ')
    }

    if (data.driverBonus && data.driverBonus < 0) {
      errors.push('Förarbonus kan inte vara negativ')
    }

    if (data.actualVAT && data.actualVAT < 0) {
      errors.push('Moms kan inte vara negativ')
    }

    // Logical validation
    if (data.grossEarnings > 0 && data.netEarnings > data.grossEarnings) {
      warnings.push('Nettoinkomst är högre än bruttoinkomst')
    }

    if (data.grossEarnings === 0 && data.netEarnings === 0) {
      warnings.push('Både brutto- och nettoinkomst är noll')
    }

    // VAT validation
    if (data.actualVAT && data.grossEarnings > 0 && data.actualVAT > data.grossEarnings) {
      warnings.push('Moms är högre än bruttoinkomst')
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    }
  }

  /**
   * Get field mapping result from unified mapping (for compatibility with legacy code)
   */
  static getFieldMappingResult(mapping: UnifiedColumnMapping): {
    mapping: Record<string, string>
    confidence: number
    unmappedFields: string[]
    requiredMissing: string[]
  } {
    return {
      mapping: mapping.earningsFields,
      confidence: mapping.confidence,
      unmappedFields: mapping.unmappedHeaders,
      requiredMissing: mapping.missingRequiredFields.filter(field =>
        ['grossEarnings', 'netEarnings', 'tips', 'driverBonus', 'actualVAT'].includes(field)
      )
    }
  }

  /**
   * Aggregate transaction data by driver name for transaction-based files
   * This is used when a file contains individual transactions that need to be grouped by driver
   */
  static aggregateTransactionsByDriver(
    data: Record<string, any>[],
    mapping: UnifiedColumnMapping
  ): Record<string, any>[] {
    if (!mapping.identifierFields.driverName) {
      console.warn('[TransactionAggregation] No driverName field mapping found, skipping aggregation')
      return data
    }

    const driverNameField = mapping.identifierFields.driverName

    // Group transactions by driver name
    const driverGroups = new Map<string, Record<string, any>[]>()

    data.forEach(row => {
      const driverName = row[driverNameField]
      if (!driverName || typeof driverName !== 'string' || driverName.trim() === '') {
        console.warn('[TransactionAggregation] Skipping row with empty driver name:', row)
        return
      }

      const normalizedDriverName = driverName.trim()
      if (!driverGroups.has(normalizedDriverName)) {
        driverGroups.set(normalizedDriverName, [])
      }
      driverGroups.get(normalizedDriverName)!.push(row)
    })

    // Aggregate earnings for each driver
    const aggregatedData: Record<string, any>[] = []

    driverGroups.forEach((transactions, driverName) => {
      const aggregatedRow: Record<string, any> = {}

      // Set driver name
      aggregatedRow[driverNameField] = driverName

      // Initialize aggregation totals
      let totalGrossEarnings = 0
      let totalNetEarnings = 0
      let totalTips = 0
      let totalDriverBonus = 0
      let totalActualVAT = 0
      let transactionCount = 0

      // Helper function to safely parse numeric values
      const parseNumber = (value: any): number => {
        const parsed = parseMoneyAmount(value)
        return parsed === null ? 0 : Math.abs(parsed)
      }

      // Sum up all transactions for this driver
      transactions.forEach(transaction => {
        transactionCount++

        // Aggregate earnings fields
        if (mapping.earningsFields.grossEarnings) {
          totalGrossEarnings += parseNumber(transaction[mapping.earningsFields.grossEarnings])
        }
        if (mapping.earningsFields.netEarnings) {
          totalNetEarnings += parseNumber(transaction[mapping.earningsFields.netEarnings])
        }
        if (mapping.earningsFields.tips) {
          totalTips += parseNumber(transaction[mapping.earningsFields.tips])
        }
        if (mapping.earningsFields.driverBonus) {
          totalDriverBonus += parseNumber(transaction[mapping.earningsFields.driverBonus])
        }
        if (mapping.earningsFields.actualVAT) {
          totalActualVAT += parseNumber(transaction[mapping.earningsFields.actualVAT])
        }

        // Copy other identifier fields from first transaction
        if (transactionCount === 1) {
          Object.keys(mapping.identifierFields).forEach(identifierKey => {
            const fieldName = mapping.identifierFields[identifierKey as keyof typeof mapping.identifierFields]
            if (fieldName && fieldName !== driverNameField && transaction[fieldName]) {
              aggregatedRow[fieldName] = transaction[fieldName]
            }
          })
        }
      })

      // Set aggregated earnings
      if (mapping.earningsFields.grossEarnings) {
        aggregatedRow[mapping.earningsFields.grossEarnings] = totalGrossEarnings.toString()
      }
      if (mapping.earningsFields.netEarnings) {
        aggregatedRow[mapping.earningsFields.netEarnings] = totalNetEarnings.toString()
      }
      if (mapping.earningsFields.tips) {
        aggregatedRow[mapping.earningsFields.tips] = totalTips.toString()
      }
      if (mapping.earningsFields.driverBonus) {
        aggregatedRow[mapping.earningsFields.driverBonus] = totalDriverBonus.toString()
      }
      if (mapping.earningsFields.actualVAT) {
        aggregatedRow[mapping.earningsFields.actualVAT] = totalActualVAT.toString()
      }

      // Add transaction count if there's a tripCount field
      if (mapping.earningsFields.tripCount) {
        aggregatedRow[mapping.earningsFields.tripCount] = transactionCount.toString()
      }

      aggregatedData.push(aggregatedRow)
    })

    return aggregatedData
  }

  /**
   * Aggregate SumUp transaction data using a clear 3-step process
   * Step 1: Classify transactions as tip or income
   * Step 2: Group by transaction ID and driver name
   * Step 3: Aggregate by driver name
   */
  static aggregateSumUpTransactionsByDriver(
    data: Record<string, any>[],
    mapping: UnifiedColumnMapping
  ): {
    aggregatedData: Record<string, any>[]
    debugData: import('@/types').AggregatedData
  } {
    if (!mapping.identifierFields.transactionId || !mapping.earningsFields.description) {
      console.warn('[SumUpAggregation] Missing required fields for SumUp aggregation, falling back to standard aggregation')
      const fallbackResult = this.aggregateTransactionsByDriver(data, mapping)
      // Return in expected format for backward compatibility
      return {
        aggregatedData: fallbackResult,
        debugData: {
          step1_classified: [],
          step2_grouped: [],
          step3_final: fallbackResult,
          processingStats: {
            originalRows: data.length,
            tipRows: 0,
            incomeRows: data.length,
            uniqueTransactions: 0,
            uniqueDrivers: fallbackResult.length,
            finalRecords: fallbackResult.length
          }
        }
      }
    }

    const transactionIdField = mapping.identifierFields.transactionId
    const descriptionField = mapping.earningsFields.description
    const driverNameField = mapping.identifierFields.driverName
    const grossEarningsField = mapping.earningsFields.grossEarnings

    // ============================================================================
    // STEP 1: CLASSIFY TRANSACTIONS AS TIP OR INCOME
    // ============================================================================
    const classifiedTransactions: import('@/types').ClassifiedTransaction[] = []
    let tipCount = 0
    let incomeCount = 0

    data.forEach(row => {
      const description = row[descriptionField]
      const isTip = description && typeof description === 'string' &&
                    description.toLowerCase().includes('tip')

      const classified: import('@/types').ClassifiedTransaction = {
        type: isTip ? 'tip' : 'income',
        originalData: row
      }

      classifiedTransactions.push(classified)

      if (isTip) {
        tipCount++
      } else {
        incomeCount++
      }
    })

    // ============================================================================
    // STEP 2: GROUP BY TRANSACTION ID AND DRIVER NAME
    // ============================================================================
    const transactionGroups: import('@/types').TransactionGroup[] = []
    const tempGroups = new Map<string, {
      incomeRecord?: Record<string, any>
      tipRecords: Record<string, any>[]
    }>()

    // Group by transaction ID first
    classifiedTransactions.forEach(classified => {
      const transactionId = classified.originalData[transactionIdField]
      if (!transactionId || typeof transactionId !== 'string' || transactionId.trim() === '') {
        console.warn('[SumUpAggregation] Skipping row with empty transaction ID:', classified.originalData)
        return
      }

      const normalizedTransactionId = transactionId.trim()
      if (!tempGroups.has(normalizedTransactionId)) {
        tempGroups.set(normalizedTransactionId, {
          tipRecords: []
        })
      }

      const group = tempGroups.get(normalizedTransactionId)!
      if (classified.type === 'income') {
        group.incomeRecord = classified.originalData
      } else {
        group.tipRecords.push(classified.originalData)
      }
    })

    // Convert to structured transaction groups with driver names
    tempGroups.forEach((group, transactionId) => {
      if (!group.incomeRecord) {
        console.warn(`[SumUpAggregation] Transaction ${transactionId} has tips but no income record, skipping`)
        return
      }

      const driverName = group.incomeRecord[driverNameField]
      if (!driverName || typeof driverName !== 'string' || driverName.trim() === '') {
        console.warn(`[SumUpAggregation] Transaction ${transactionId} has no valid driver name, skipping`)
        return
      }

      const totalIncome = this.parseNumber(group.incomeRecord[grossEarningsField])
      const totalTips = group.tipRecords.reduce((sum, tip) =>
        sum + this.parseNumber(tip[grossEarningsField]), 0
      )

      const transactionGroup: import('@/types').TransactionGroup = {
        transactionId,
        driverName: driverName.trim(),
        incomeRecord: group.incomeRecord,
        tipRecords: group.tipRecords,
        totalIncome,
        totalTips
      }

      transactionGroups.push(transactionGroup)
    })

    // ============================================================================
    // STEP 3: AGGREGATE BY DRIVER NAME
    // ============================================================================
    const driverGroups = new Map<string, import('@/types').TransactionGroup[]>()

    // Group transaction groups by driver name
    transactionGroups.forEach(group => {
      if (!driverGroups.has(group.driverName)) {
        driverGroups.set(group.driverName, [])
      }
      driverGroups.get(group.driverName)!.push(group)
    })

    // Create final aggregated records
    const aggregatedData: Record<string, any>[] = []

    driverGroups.forEach((groups, driverName) => {
      const aggregatedRow: Record<string, any> = {}

      // Set driver name
      aggregatedRow[driverNameField] = driverName

      // Initialize aggregation totals
      let totalGrossEarnings = 0
      let totalNetEarnings = 0
      let totalTips = 0
      let totalDriverBonus = 0
      let totalActualVAT = 0
      let transactionCount = 0

      // Sum up all transaction groups for this driver
      groups.forEach(group => {
        transactionCount++

        // Add income from the income record
        totalGrossEarnings += this.parseNumber(group.incomeRecord[mapping.earningsFields.grossEarnings])

        if (mapping.earningsFields.netEarnings) {
          totalNetEarnings += this.parseNumber(group.incomeRecord[mapping.earningsFields.netEarnings])
        }
        if (mapping.earningsFields.driverBonus) {
          totalDriverBonus += this.parseNumber(group.incomeRecord[mapping.earningsFields.driverBonus])
        }
        if (mapping.earningsFields.actualVAT) {
          totalActualVAT += this.parseNumber(group.incomeRecord[mapping.earningsFields.actualVAT])
        }

        // Add tips from tip records
        totalTips += group.totalTips

        // Copy other identifier fields from first transaction
        if (transactionCount === 1) {
          Object.keys(mapping.identifierFields).forEach(identifierKey => {
            const fieldName = mapping.identifierFields[identifierKey as keyof typeof mapping.identifierFields]
            if (fieldName && fieldName !== driverNameField && group.incomeRecord[fieldName]) {
              aggregatedRow[fieldName] = group.incomeRecord[fieldName]
            }
          })
        }
      })

      // Set aggregated earnings in the final row
      if (mapping.earningsFields.grossEarnings) {
        aggregatedRow[mapping.earningsFields.grossEarnings] = totalGrossEarnings.toString()
      }
      if (mapping.earningsFields.netEarnings) {
        aggregatedRow[mapping.earningsFields.netEarnings] = totalNetEarnings.toString()
      }
      if (mapping.earningsFields.tips) {
        aggregatedRow[mapping.earningsFields.tips] = totalTips.toString()
      }
      if (mapping.earningsFields.driverBonus) {
        aggregatedRow[mapping.earningsFields.driverBonus] = totalDriverBonus.toString()
      }
      if (mapping.earningsFields.actualVAT) {
        aggregatedRow[mapping.earningsFields.actualVAT] = totalActualVAT.toString()
      }

      // Add transaction count if there's a tripCount field
      if (mapping.earningsFields.tripCount) {
        aggregatedRow[mapping.earningsFields.tripCount] = transactionCount.toString()
      }

      aggregatedData.push(aggregatedRow)
    })

    // Create debug data for transparency
    const debugData: import('@/types').AggregatedData = {
      step1_classified: classifiedTransactions,
      step2_grouped: transactionGroups,
      step3_final: aggregatedData,
      processingStats: {
        originalRows: data.length,
        tipRows: tipCount,
        incomeRows: incomeCount,
        uniqueTransactions: transactionGroups.length,
        uniqueDrivers: driverGroups.size,
        finalRecords: aggregatedData.length
      }
    }

    return {
      aggregatedData,
      debugData
    }
  }

  /**
   * Helper method to safely parse numeric values
   */
  private static parseNumber(value: any): number {
    const parsed = parseMoneyAmount(value)
    return parsed === null ? 0 : Math.abs(parsed)
  }

  /**
   * Detect if data represents individual transactions (multiple rows per driver) vs aggregated earnings
   */
  static isTransactionData(
    data: Record<string, any>[],
    mapping: UnifiedColumnMapping
  ): boolean {
    if (!mapping.identifierFields.driverName || data.length < 2) {
      return false
    }

    const driverNameField = mapping.identifierFields.driverName
    const driverNames = new Set<string>()

    for (const row of data) {
      const driverName = row[driverNameField]
      if (driverName && typeof driverName === 'string' && driverName.trim() !== '') {
        const normalizedName = driverName.trim()
        if (driverNames.has(normalizedName)) {
          // Found duplicate driver name, this indicates transaction data
          return true
        }
        driverNames.add(normalizedName)
      }
    }

    return false
  }

  /**
   * Detect if data is SumUp transaction data that needs special tip processing
   */
  static isSumUpTransactionData(
    data: Record<string, any>[],
    mapping: UnifiedColumnMapping,
    detectedPlatform?: string
  ): boolean {
    // Must be detected as SumUp platform
    if (detectedPlatform !== 'sumup' && mapping.detectedPlatform !== 'sumup') {
      return false
    }

    // Must have transaction ID and description fields
    if (!mapping.identifierFields.transactionId || !mapping.earningsFields.description) {
      return false
    }

    const transactionIdField = mapping.identifierFields.transactionId
    const descriptionField = mapping.earningsFields.description

    // Look for tip entries in the data
    const hasTips = data.some(row => {
      const description = row[descriptionField]
      return description && typeof description === 'string' &&
             description.toLowerCase().includes('tip')
    })

    // Look for duplicate transaction IDs (indicating multiple rows per transaction)
    const transactionIds = new Set<string>()
    const hasDuplicateTransactionIds = data.some(row => {
      const transactionId = row[transactionIdField]
      if (transactionId && typeof transactionId === 'string' && transactionId.trim() !== '') {
        const normalized = transactionId.trim()
        if (transactionIds.has(normalized)) {
          return true
        }
        transactionIds.add(normalized)
      }
      return false
    })

    return hasTips && hasDuplicateTransactionIds
  }
}